// src/pages/admin/RoleCapabilityManager.tsx - COMPLETE FIXED VERSION
import React, { useState, useEffect } from 'react';
import {
  Shield, Search, ChevronDown, ChevronUp,
  RefreshCw, Check, AlertCircle, Save, Eye,
  FileText, Calendar, Settings,
} from 'lucide-react';
import { Button, Card, Breadcrumb, Modal, Loading, LogoLoadingMark, LoadingLabel } from '../../components/ui';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store/authStore';

// ========== TYPES ==========

interface RoleWithCaps {
  id: number;
  name: string;
  display_name: string;
  level: number;
  capabilities: string[];
}

interface CapabilityItem {
  value: string;
  label: string;
  description: string;
}

interface CapabilityGroup {
  name: string;
  icon: React.ReactNode;
  iconBg: string;
  description: string;
  capabilities: CapabilityItem[];
}

// ========== CAPABILITY GROUPS ==========

const CAPABILITY_GROUPS: CapabilityGroup[] = [
  {
    name: 'Laporan',
    icon: <FileText className="w-4 h-4" />,
    iconBg: 'bg-blue-500',
    description: 'Akses untuk membuat dan mengelola laporan masalah',
    capabilities: [
      { value: 'create_laporan', label: 'Buat Laporan', description: 'Membuat laporan masalah baru' },
      { value: 'view_all_reports', label: 'Lihat Semua Laporan', description: 'Akses ke semua laporan di sistem' },
      { value: 'access_report_builder', label: 'Report Builder', description: 'Akses fitur laporan & analitik' },
      { value: 'archive', label: 'Arsipkan Laporan', description: 'Mengarsipkan laporan yang selesai' },
    ],
  },
  {
    name: 'Validasi',
    icon: <Shield className="w-4 h-4" />,
    iconBg: 'bg-purple-500',
    description: 'Akses untuk memvalidasi, mengeskalasi, dan menyelesaikan laporan',
    capabilities: [
      { value: 'validate', label: 'Validasi', description: 'Melakukan validasi laporan' },
      { value: 'resolve_local', label: 'Selesaikan Lokal', description: 'Menyelesaikan masalah di level sendiri' },
      { value: 'escalate', label: 'Eskalasi', description: 'Meneruskan ke level lebih tinggi' },
    ],
  },
  {
    name: 'RAPIM',
    icon: <Calendar className="w-4 h-4" />,
    iconBg: 'bg-indigo-500',
    description: 'Akses untuk mengelola rapat pimpinan',
    capabilities: [
      { value: 'manage_rapim', label: 'Kelola RAPIM', description: 'Mengelola rapat pimpinan' },
      { value: 'schedule_rapim', label: 'Jadwalkan RAPIM', description: 'Membuat jadwal rapat baru' },
      { value: 'make_rapim_decision', label: 'Keputusan RAPIM', description: 'Membuat keputusan dalam rapat' },
    ],
  },
  {
    name: 'Monitoring',
    icon: <Eye className="w-4 h-4" />,
    iconBg: 'bg-teal-500',
    description: 'Akses untuk monitoring dan tindak lanjut laporan',
    capabilities: [
      { value: 'access_monitoring', label: 'Akses Monitoring', description: 'Melihat dashboard monitoring' },
      { value: 'change_pic', label: 'Ganti PIC', description: 'Mengubah penanggung jawab tugas' },
      { value: 'extend_deadline', label: 'Perpanjang Deadline', description: 'Memperpanjang batas waktu penyelesaian' },
      { value: 'confirm_completion', label: 'Konfirmasi Selesai', description: 'Mengkonfirmasi penyelesaian tugas' },
      { value: 'return_to_rapim', label: 'Kembalikan ke RAPIM', description: 'Mengembalikan untuk dibahas ulang' },
      { value: 'send_reminder', label: 'Kirim Pengingat', description: 'Mengirim notifikasi pengingat' },
    ],
  },
  {
    name: 'Administrasi',
    icon: <Settings className="w-4 h-4" />,
    iconBg: 'bg-rose-500',
    description: 'Akses untuk mengelola konfigurasi sistem',
    capabilities: [
      { value: 'manage_users', label: 'Kelola User', description: 'Mengelola akun pengguna' },
      { value: 'manage_master_data', label: 'Master Data', description: 'Mengelola data referensi' },
      { value: 'manage_settings', label: 'Pengaturan', description: 'Mengubah konfigurasi sistem' },
      { value: 'manage_units', label: 'Kelola Unit', description: 'Mengelola struktur organisasi' },
      { value: 'view_activity_log', label: 'Log Aktivitas', description: 'Melihat riwayat perubahan' },
    ],
  },
];

// ========== LEVEL BADGE CONFIG ==========

const getLevelBadge = (level: number) => {
  if (level === 0) return { color: 'bg-rose-100 text-rose-700 border-rose-200', label: 'Pimpinan Tertinggi', icon: '👑' };
  if (level === 1) return { color: 'bg-purple-100 text-purple-700 border-purple-200', label: 'Pimpinan Universitas', icon: '🏛️' };
  if (level === 2) return { color: 'bg-blue-100 text-blue-700 border-blue-200', label: 'Pimpinan Fakultas/Lembaga', icon: '🏫' };
  if (level === 3) return { color: 'bg-teal-100 text-teal-700 border-teal-200', label: 'Pimpinan Program/Unit', icon: '📋' };
  if (level === 4) return { color: 'bg-amber-100 text-amber-700 border-amber-200', label: 'Kepala Bagian', icon: '📁' };
  if (level === 5) return { color: 'bg-cyan-100 text-cyan-700 border-cyan-200', label: 'Kepala Sub-Unit', icon: '📂' };
  return { color: 'bg-gray-100 text-gray-600 border-gray-200', label: 'Staff / Pelaksana', icon: '👤' };
};

// ========== CAPABILITY RECOMMENDATIONS PER LEVEL ==========

const RECOMMENDED_CAPS: Record<number, string[]> = {
  0: [ // Level 0: Rektor, Admin — akses penuh
    'create_laporan', 'validate', 'resolve_local', 'escalate',
    'manage_rapim', 'schedule_rapim', 'make_rapim_decision',
    'access_monitoring', 'change_pic', 'extend_deadline',
    'confirm_completion', 'return_to_rapim', 'send_reminder',
    'view_all_reports', 'access_report_builder', 'archive',
    'manage_users', 'manage_master_data', 'manage_settings',
    'manage_units', 'view_activity_log',
  ],
  1: [ // Level 1: Warek, Sekretaris, Senat — pimpinan universitas
    'validate', 'resolve_local', 'escalate',
    'manage_rapim', 'schedule_rapim', 'make_rapim_decision',
    'change_pic', 'extend_deadline', 'confirm_completion',
    'return_to_rapim', 'send_reminder',
    'access_monitoring', 'access_report_builder', 'view_all_reports',
  ],
  2: [ // Level 2: Dekan, Kepala Lembaga, LPM — pimpinan fakultas/lembaga
    'validate', 'resolve_local', 'escalate',
    'access_monitoring', 'access_report_builder',
  ],
  3: [ // Level 3: Kaprodi, Kalem (jika level 3) — pimpinan unit
    'create_laporan',
    'validate', 'resolve_local',
  ],
  4: [ // Level 4: Kabag
    'create_laporan',
  ],
  5: [ // Level 5: Ka TU, Kalab
    'create_laporan',
  ],
  6: [ // Level 6: Staff
    'create_laporan',
  ],
};

const UNUSUAL_WARNINGS: Record<string, string> = {
  'manage_rapim': 'Mengelola RAPIM biasanya hanya untuk Rektor, Warek, atau Sekretaris',
  'make_rapim_decision': 'Membuat keputusan RAPIM biasanya hanya untuk Rektor',
  'manage_users': 'Mengelola user biasanya hanya untuk Administrator',
  'manage_settings': 'Mengubah pengaturan sistem biasanya hanya untuk Administrator',
  'manage_units': 'Mengelola unit organisasi biasanya hanya untuk Administrator',
  'view_activity_log': 'Melihat log aktivitas biasanya hanya untuk Administrator',
  'manage_master_data': 'Mengelola master data biasanya hanya untuk Administrator',
  'confirm_completion': 'Konfirmasi selesai biasanya hanya untuk Rektor atau Admin',
  'change_pic': 'Mengganti PIC biasanya hanya untuk pimpinan (Rektor/Warek)',
  'return_to_rapim': 'Mengembalikan ke RAPIM biasanya hanya untuk pimpinan',
  'send_reminder': 'Mengirim pengingat biasanya hanya untuk pimpinan',
  'archive': 'Mengarsipkan laporan biasanya hanya untuk Admin',
  'extend_deadline': 'Memperpanjang deadline biasanya hanya untuk pimpinan',
};

const getUnusualCaps = (
  roleLevel: number,
  selectedCaps: Set<string>,
  roleName: string
): { cap: string; warning: string }[] => {
  const recommended = RECOMMENDED_CAPS[roleLevel] || RECOMMENDED_CAPS[6] || [];
  const warnings: { cap: string; warning: string }[] = [];

  selectedCaps.forEach(cap => {
    // Admin boleh semua — skip warning
    if (roleName === 'admin') return;

    // Jangan warning jika capability ada di rekomendasi level ini
    if (recommended.includes(cap)) return;

    if (UNUSUAL_WARNINGS[cap]) {
      warnings.push({ cap, warning: UNUSUAL_WARNINGS[cap] });
    }
  });

  return warnings;
};

// ========== COMPONENT ==========

const RoleCapabilityManager: React.FC = () => {
  const [roles, setRoles] = useState<RoleWithCaps[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState<RoleWithCaps | null>(null);
  const [editCaps, setEditCaps] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    new Set(CAPABILITY_GROUPS.map(g => g.name))
  );
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const { user, setCapabilities, setUser } = useAuthStore();

  // ========== DATA FETCHING ==========

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const rolesRes = await api.get('/admin/roles');
      const rolesData = rolesRes.data?.data || rolesRes.data || [];
      const rolesList = Array.isArray(rolesData) ? rolesData : [];

      const rolesWithCaps: RoleWithCaps[] = [];

      for (const role of rolesList) {
        let caps: string[] = [];
        try {
          const capRes = await api.get(`/admin/roles/${role.id}`);
          const roleData = capRes.data?.data || capRes.data || role;
          caps = (roleData.capabilities || []).map((c: any) =>
            typeof c === 'string' ? c : c.capability || c.name || ''
          ).filter(Boolean);
        } catch {
          // Silently fail
        }

        rolesWithCaps.push({
          id: role.id,
          name: role.name,
          display_name: role.display_name,
          level: role.level ?? 99,
          capabilities: caps,
        });
      }

      setRoles(rolesWithCaps.sort((a, b) => a.level - b.level));
    } catch (err) {
      console.error('Failed to load roles:', err);
      toast.error('Gagal memuat data role');
    } finally {
      setLoading(false);
    }
  };

  // ========== MODAL HANDLERS ==========

  const openEditModal = (role: RoleWithCaps) => {
    if (role.name === 'admin') {
      toast('Admin otomatis memiliki semua capabilities', { icon: 'ℹ️' });
      return;
    }
    setSelectedRole(role);
    setEditCaps(new Set(role.capabilities));
    setExpandedGroups(new Set(CAPABILITY_GROUPS.map(g => g.name)));

    // ✅ Info rekomendasi saat buka modal
    const recommended = RECOMMENDED_CAPS[role.level] || [];
    const currentCaps = role.capabilities;
    const missingRecommended = recommended.filter(cap => !currentCaps.includes(cap));

    if (missingRecommended.length > 0 && currentCaps.length === 0) {
      const levelBadge = getLevelBadge(role.level);
      toast(
        `💡 Rekomendasi: "${role.display_name}" berada di level ${role.level} (${levelBadge.label}). ` +
        `Role di level ini biasanya memiliki ${recommended.length} capabilities. ` +
        `Klik "Isi Rekomendasi" untuk mengisi otomatis.`,
        { duration: 6000, icon: '💡' }
      );
    }
  };

  const closeModal = () => {
    setSelectedRole(null);
    setEditCaps(new Set());
  };

  // ========== CAPABILITY TOGGLES ==========

  const toggleCap = (cap: string) => {
    setEditCaps((prev) => {
      const next = new Set(prev);
      next.has(cap) ? next.delete(cap) : next.add(cap);
      return next;
    });
  };

  const toggleGroup = (groupName: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      next.has(groupName) ? next.delete(groupName) : next.add(groupName);
      return next;
    });
  };

  const selectAllInGroup = (group: CapabilityGroup) => {
    setEditCaps((prev) => {
      const next = new Set(prev);
      group.capabilities.forEach((cap) => next.add(cap.value));
      return next;
    });
  };

  const deselectAllInGroup = (group: CapabilityGroup) => {
    setEditCaps((prev) => {
      const next = new Set(prev);
      group.capabilities.forEach((cap) => next.delete(cap.value));
      return next;
    });
  };

  const selectAll = () => {
    const allCaps = CAPABILITY_GROUPS.flatMap(g => g.capabilities.map(c => c.value));
    setEditCaps(new Set(allCaps));
  };

  const deselectAll = () => {
    if (!selectedRole) return;

    const currentCount = editCaps.size;
    if (currentCount > 0) {
      const confirmed = window.confirm(
        `Yakin ingin menghapus semua ${currentCount} capabilities dari "${selectedRole.display_name}"?\n\n` +
        `⚠️ User dengan role ini akan kehilangan semua akses fitur!`
      );
      if (!confirmed) return;
    }

    setEditCaps(new Set());
  };

  // ========== SAVE ==========

  const handleSave = async () => {
    if (!selectedRole) return;

    // ✅ Warning jika ada capability tidak lazim
    const unusualCaps = getUnusualCaps(selectedRole.level, editCaps, selectedRole.name);
    if (unusualCaps.length > 0) {
      const capList = unusualCaps.map(u => `• ${u.cap.replace(/_/g, ' ')}`).join('\n');
      const confirmed = window.confirm(
        `⚠️ PERHATIAN: Ada ${unusualCaps.length} capability yang tidak lazim untuk role "${selectedRole.display_name}" (Level ${selectedRole.level}):\n\n` +
        `${capList}\n\n` +
        `Capability ini biasanya diberikan ke role dengan level lebih tinggi.\n` +
        `Yakin ingin menyimpan?`
      );
      if (!confirmed) return;
    }

    const changes = getChangeSummary();

    // WARNING IF REMOVING MANY CAPS
    if (changes.removed > 3) {
      const confirmed = window.confirm(
        `Anda akan menghapus ${changes.removed} capabilities dari "${selectedRole.display_name}".\n\n` +
        `⚠️ User dengan role ini mungkin kehilangan akses ke beberapa fitur.\n` +
        `Lanjutkan?`
      );
      if (!confirmed) return;
    }

    // WARNING IF RESULT IS 0 CAPS
    if (editCaps.size === 0 && selectedRole.capabilities.length > 0) {
      const confirmed = window.confirm(
        `⚠️ PERINGATAN: "${selectedRole.display_name}" tidak akan memiliki capabilities apapun!\n\n` +
        `User dengan role ini tidak akan bisa mengakses fitur sistem.\n` +
        `Yakin ingin melanjutkan?`
      );
      if (!confirmed) return;
    }

    setSaving(true);

    try {
      const currentCaps = selectedRole.capabilities;
      const newCaps = Array.from(editCaps);

      // Remove unchecked
      for (const cap of currentCaps) {
        if (!newCaps.includes(cap)) {
          await api.delete(`/admin/role-capabilities/${selectedRole.id}/${cap}`).catch(() => { });
        }
      }

      // Add new
      for (const cap of newCaps) {
        if (!currentCaps.includes(cap)) {
          await api.post('/admin/role-capabilities', {
            role_id: selectedRole.id,
            capability: cap,
          }).catch(() => { });
        }
      }

      toast.success(`Capabilities untuk ${selectedRole.display_name} berhasil diperbarui`);

      // Auto refresh if editing own role
      if (user?.role?.id === selectedRole.id) {
        await refreshOwnCapabilities();
      }

      closeModal();
      fetchRoles();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  // ========== REFRESH OWN CAPABILITIES ==========

  const refreshOwnCapabilities = async () => {
    setRefreshing(true);
    try {
      const res = await api.get('/user');
      const data = res.data?.data;

      if (data) {
        const userObj = {
          id: data.id,
          name: data.name,
          email: data.email,
          phone: data.phone,
          avatar: data.avatar,
          is_active: data.is_active,
          last_login_at: data.last_login_at,
          role: data.role,
          unit: data.unit,
        };
        const storedCaps = JSON.parse(localStorage.getItem('capabilities') || '[]');
        const caps = Array.isArray(data.capabilities) ? data.capabilities : storedCaps;

        setUser(userObj as any);
        setCapabilities(caps as any);
        setTimeout(() => {
          window.location.reload();
        }, 500);
        toast.success(
          'Capabilities Anda diperbarui! Refresh halaman untuk melihat perubahan sidebar.',
          { duration: 5000, icon: '🔄' }
        );
      }
    } catch (err) {
      console.error('Failed to refresh capabilities:', err);
    } finally {
      setRefreshing(false);
    }
  };

  // ========== COMPUTED ==========

  const filteredRoles = roles.filter((r) =>
    r.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.display_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group roles by level
  const groupedRoles: Record<string, RoleWithCaps[]> = {};
  filteredRoles.forEach((role) => {
    const badge = getLevelBadge(role.level);
    const key = badge.label;
    if (!groupedRoles[key]) groupedRoles[key] = [];
    groupedRoles[key].push(role);
  });

  // Count changes
  const getChangeSummary = () => {
    if (!selectedRole) return { added: 0, removed: 0, unchanged: 0 };
    const currentArr = selectedRole.capabilities;
    const nextArr = Array.from(editCaps);
    const added = nextArr.filter(c => !currentArr.includes(c)).length;
    const removed = currentArr.filter(c => !editCaps.has(c)).length;
    const unchanged = currentArr.filter(c => editCaps.has(c)).length;
    return { added, removed, unchanged };
  };

  // ========== RENDER ==========

  if (loading) {
    return <Loading variant="page" message="Memuat data role & capabilities..." />;
  }

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[
        { label: 'Admin', href: '/admin' },
        { label: 'Role Capabilities' },
      ]} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-teal-dark tracking-tight flex items-center gap-2">
            <Shield className="w-6 h-6 text-indigo-500" />
            Role Capabilities
          </h1>
          <p className="text-sm text-neutral-text-secondary mt-1">
            Kelola kemampuan akses untuk setiap role — tanpa hardcode
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={refreshOwnCapabilities}
            disabled={refreshing}
            icon={refreshing ? <LogoLoadingMark size="xs" /> : <RefreshCw className="w-4 h-4" />}
          >
            {refreshing ? <LoadingLabel label="Memuat..." /> : 'Refresh My Caps'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchRoles}
            disabled={loading}
            icon={<RefreshCw className="w-4 h-4" />}
          >
            Reload
          </Button>
        </div>
      </div>

      {/* Info Banner */}
      <Card className="!bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-semibold mb-1">💡 Cara Kerja Capabilities</p>
            <ul className="text-xs space-y-1 text-blue-700">
              <li>• <strong>Admin</strong> otomatis memiliki semua capabilities (tidak perlu di-set)</li>
              <li>• Klik kartu role untuk mengelola capabilities</li>
              <li>• Perubahan berlaku setelah user <strong>login ulang</strong></li>
              <li>• Untuk role Anda sendiri, klik <strong>"Refresh My Caps"</strong> lalu refresh halaman</li>
            </ul>
          </div>
        </div>
      </Card>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-text-secondary" />
        <input
          type="text"
          placeholder="Cari role..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 text-sm border border-neutral-border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-main/20 focus:border-teal-main transition-all"
        />
      </div>

      {/* Role Cards Grouped by Level */}
      {Object.entries(groupedRoles).length === 0 ? (
        <Card className="text-center py-12">
          <Shield className="w-12 h-12 mx-auto mb-3 text-neutral-text-secondary opacity-30" />
          <p className="text-base font-bold">Tidak ada role ditemukan</p>
          {searchTerm && (
            <Button variant="ghost" size="sm" className="mt-2" onClick={() => setSearchTerm('')}>
              Reset Pencarian
            </Button>
          )}
        </Card>
      ) : (
        Object.entries(groupedRoles).map(([groupLabel, groupRoles]) => {
          const firstRole = groupRoles[0];
          const badge = getLevelBadge(firstRole.level);

          return (
            <div key={groupLabel}>
              {/* Level Group Header */}
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">{badge.icon}</span>
                <h2 className="text-xs font-bold uppercase tracking-widest text-neutral-text-secondary">
                  {groupLabel}
                </h2>
                <span className="text-[10px] text-neutral-text-secondary">
                  Level {firstRole.level} • {groupRoles.length} role
                </span>
                <div className="flex-1 h-px bg-neutral-border ml-2" />
              </div>

              {/* Role Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {groupRoles.map((role) => {
                  const isProtected = role.name === 'admin';
                  const levelBadge = getLevelBadge(role.level);

                  return (
                    <div
                      key={role.id}
                      onClick={() => openEditModal(role)}
                      className="cursor-pointer"
                    >
                      <Card
                        hover={!isProtected}
                        className={`
                          transition-all group
                          ${isProtected ? 'border-l-4 border-l-rose-400 opacity-80' : 'hover:shadow-lg hover:-translate-y-0.5'}
                        `}
                      >
                        {/* Header */}
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className={`
                            w-10 h-10 rounded-xl flex items-center justify-center shrink-0 shadow-md
                            ${isProtected
                                ? 'bg-gradient-to-br from-rose-500 to-rose-600'
                                : 'bg-gradient-to-br from-indigo-500 to-indigo-600'
                              }
                          `}>
                              <Shield className="w-5 h-5 text-white" />
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="text-sm font-bold text-neutral-text-primary group-hover:text-teal-main transition-colors">
                                  {role.display_name}
                                </h3>
                                {isProtected && (
                                  <span className="text-[9px] px-1.5 py-0.5 bg-rose-100 text-rose-600 rounded font-bold uppercase">
                                    Auto
                                  </span>
                                )}
                              </div>
                              <p className="text-[10px] text-neutral-text-secondary font-mono">{role.name}</p>
                            </div>
                          </div>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-lg border ${levelBadge.color}`}>
                            Lv.{role.level}
                          </span>
                        </div>

                        {/* Capabilities Count */}
                        <div className="mt-4 pt-3 border-t border-neutral-border/50">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-neutral-text-secondary">
                              Capabilities
                            </span>
                            <span className={`text-sm font-bold ${isProtected ? 'text-rose-500' : role.capabilities.length > 0 ? 'text-teal-main' : 'text-neutral-text-secondary'
                              }`}>
                              {isProtected ? 'ALL' : role.capabilities.length}
                            </span>
                          </div>

                          {/* Capability Preview Tags */}
                          {isProtected ? (
                            <p className="text-[10px] text-rose-600 italic">
                              Admin memiliki semua capabilities secara otomatis
                            </p>
                          ) : role.capabilities.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {role.capabilities.slice(0, 5).map((cap) => (
                                <span
                                  key={cap}
                                  className="px-1.5 py-0.5 bg-teal-50 text-teal-700 text-[9px] rounded font-medium border border-teal-200"
                                >
                                  {cap.replace(/_/g, ' ')}
                                </span>
                              ))}
                              {role.capabilities.length > 5 && (
                                <span className="px-1.5 py-0.5 bg-neutral-bg text-neutral-text-secondary text-[9px] rounded font-medium">
                                  +{role.capabilities.length - 5} lagi
                                </span>
                              )}
                            </div>
                          ) : (
                            <p className="text-[10px] text-neutral-text-secondary italic">
                              Belum ada capabilities
                            </p>
                          )}
                        </div>
                      </Card>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}

      {/* ========== EDIT MODAL ========== */}
      <Modal
        isOpen={!!selectedRole}
        onClose={closeModal}
        title={`Capabilities: ${selectedRole?.display_name || ''}`}
        size="lg"
      >
        {selectedRole && (() => {
          const role = selectedRole;
          const changes = getChangeSummary();
          const hasChanges = changes.added > 0 || changes.removed > 0;
          const unusualCaps = getUnusualCaps(role.level, editCaps, role.name);

          return (
            <div className="space-y-4">
              {/* Role Info Header */}
              <div className="flex items-center justify-between p-3 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-md">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-indigo-900">{role.display_name}</p>
                    <p className="text-[10px] text-indigo-600">
                      Level {role.level} • {editCaps.size} capabilities dipilih
                    </p>
                  </div>
                </div>

                {/* Change Summary */}
                {hasChanges && (
                  <div className="flex items-center gap-2">
                    {changes.added > 0 && (
                      <span className="text-[10px] font-bold px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
                        +{changes.added}
                      </span>
                    )}
                    {changes.removed > 0 && (
                      <span className="text-[10px] font-bold px-2 py-0.5 bg-red-100 text-red-700 rounded-full">
                        -{changes.removed}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* ✅ Smart Warning: Unusual capabilities for this role */}
              {unusualCaps.length > 0 && (
                <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-xl">
                  <div className="flex items-start gap-3">
                    <div className="p-1.5 bg-amber-100 rounded-lg shrink-0 mt-0.5">
                      <AlertCircle className="w-4 h-4 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-bold text-amber-800">
                        ⚠️ Capability Tidak Lazim untuk {role.display_name}
                      </p>
                      <p className="text-xs text-amber-700 mt-1">
                        Beberapa capability yang dipilih biasanya tidak diberikan ke role dengan level {role.level} ({getLevelBadge(role.level).label}).
                        Pastikan ini memang disengaja.
                      </p>
                      <div className="mt-3 space-y-2">
                        {unusualCaps.map(({ cap, warning }) => (
                          <div key={cap} className="flex items-start gap-2 p-2 bg-white/60 rounded-lg">
                            <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-1.5 shrink-0" />
                            <div>
                              <p className="text-xs font-semibold text-amber-900">
                                {cap.replace(/_/g, ' ')}
                              </p>
                              <p className="text-[10px] text-amber-700">{warning}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Bulk Actions */}
              <div className="flex items-center gap-2 text-xs">
                <span className="text-neutral-text-secondary font-semibold">Aksi Cepat:</span>
                <button
                  onClick={selectAll}
                  className="px-2.5 py-1 bg-teal-50 text-teal-700 rounded-lg font-semibold hover:bg-teal-100 transition-colors border border-teal-200"
                >
                  Pilih Semua
                </button>
                <button
                  onClick={deselectAll}
                  className="px-2.5 py-1 bg-red-50 text-red-700 rounded-lg font-semibold hover:bg-red-100 transition-colors border border-red-200"
                >
                  Hapus Semua
                </button>
                {/* ✅ Tombol Rekomendasi */}
                <button
                  onClick={() => {
                    const recommended = RECOMMENDED_CAPS[role.level] || [];
                    if (recommended.length === 0) {
                      toast('Tidak ada rekomendasi untuk level ini', { icon: 'ℹ️' });
                      return;
                    }
                    setEditCaps(new Set(recommended));
                    toast.success(`${recommended.length} capabilities diisi sesuai rekomendasi Level ${role.level}`);
                  }}
                  className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-lg font-semibold hover:bg-blue-100 transition-colors border border-blue-200"
                >
                  💡 Isi Rekomendasi
                </button>
              </div>

              {/* Capability Groups */}
              <div className="max-h-[55vh] overflow-y-auto space-y-3 pr-1">
                {CAPABILITY_GROUPS.map((group) => {
                  const isExpanded = expandedGroups.has(group.name);
                  const selectedInGroup = group.capabilities.filter(c => editCaps.has(c.value)).length;
                  const totalInGroup = group.capabilities.length;
                  const allSelected = selectedInGroup === totalInGroup;
                  const noneSelected = selectedInGroup === 0;

                  return (
                    <div
                      key={group.name}
                      className={`
                        border rounded-2xl overflow-hidden transition-all
                        ${isExpanded ? 'border-teal-main/30 shadow-sm' : 'border-neutral-border'}
                      `}
                    >
                      {/* Group Header */}
                      <button
                        onClick={() => toggleGroup(group.name)}
                        className="w-full flex items-center justify-between p-4 bg-neutral-bg/50 hover:bg-neutral-bg transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 ${group.iconBg} rounded-lg flex items-center justify-center text-white shadow-sm`}>
                            {group.icon}
                          </div>
                          <div className="text-left">
                            <p className="text-sm font-bold text-neutral-text-primary">{group.name}</p>
                            <p className="text-[10px] text-neutral-text-secondary">{group.description}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {/* Progress Indicator */}
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-1.5 bg-neutral-border rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all ${allSelected ? 'bg-teal-main' : noneSelected ? 'bg-neutral-border' : 'bg-amber-400'
                                  }`}
                                style={{ width: `${(selectedInGroup / totalInGroup) * 100}%` }}
                              />
                            </div>
                            <span className={`text-xs font-bold min-w-[32px] text-right ${allSelected ? 'text-teal-main' : noneSelected ? 'text-neutral-text-secondary' : 'text-amber-600'
                              }`}>
                              {selectedInGroup}/{totalInGroup}
                            </span>
                          </div>
                          {isExpanded
                            ? <ChevronUp className="w-4 h-4 text-neutral-text-secondary" />
                            : <ChevronDown className="w-4 h-4 text-neutral-text-secondary" />
                          }
                        </div>
                      </button>

                      {/* Group Content */}
                      {isExpanded && (
                        <div className="p-4 space-y-3 animate-in">
                          {/* Group Actions */}
                          <div className="flex items-center gap-2 pb-2 border-b border-neutral-border/50">
                            <button
                              onClick={() => selectAllInGroup(group)}
                              className="text-[10px] font-semibold text-teal-main hover:underline"
                            >
                              ✓ Pilih Semua
                            </button>
                            <span className="text-neutral-text-secondary text-[10px]">|</span>
                            <button
                              onClick={() => deselectAllInGroup(group)}
                              className="text-[10px] font-semibold text-danger hover:underline"
                            >
                              ✗ Hapus Semua
                            </button>
                          </div>

                          {/* Capabilities */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {group.capabilities.map((cap) => {
                              const isChecked = editCaps.has(cap.value);
                              const wasOriginal = role.capabilities.includes(cap.value);
                              const isNew = isChecked && !wasOriginal;
                              const isRemoved = !isChecked && wasOriginal;

                              return (
                                <label
                                  key={cap.value}
                                  className={`
                                    flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all
                                    ${isChecked
                                      ? 'bg-teal-50/70 border-teal-400 shadow-sm'
                                      : 'border-neutral-border hover:border-teal-200 hover:bg-neutral-bg/50'
                                    }
                                  `}
                                >
                                  {/* Custom Checkbox */}
                                  <div className={`
                                    w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 mt-0.5 transition-all
                                    ${isChecked
                                      ? 'bg-teal-main border-teal-main shadow-sm'
                                      : 'border-neutral-border bg-white'
                                    }
                                  `}>
                                    {isChecked && <Check className="w-3 h-3 text-white" />}
                                  </div>
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => toggleCap(cap.value)}
                                    className="sr-only"
                                  />

                                  {/* Label */}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <p className={`text-sm font-semibold ${isChecked ? 'text-teal-dark' : 'text-neutral-text-primary'
                                        }`}>
                                        {cap.label}
                                      </p>
                                      {isNew && (
                                        <span className="text-[8px] font-bold px-1 py-0.5 bg-green-100 text-green-700 rounded uppercase">
                                          new
                                        </span>
                                      )}
                                      {isRemoved && (
                                        <span className="text-[8px] font-bold px-1 py-0.5 bg-red-100 text-red-700 rounded uppercase">
                                          removed
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-[10px] text-neutral-text-secondary mt-0.5">
                                      {cap.description}
                                    </p>
                                    <code className="text-[9px] font-mono text-neutral-text-secondary/60 bg-neutral-bg px-1 py-0.5 rounded mt-1 inline-block">
                                      {cap.value}
                                    </code>
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between pt-4 border-t border-neutral-border">
                <div className="flex items-center gap-3 text-xs text-neutral-text-secondary">
                  <span className="font-semibold">{editCaps.size} capabilities dipilih</span>
                  {hasChanges && (
                    <span className="text-amber-600 font-semibold">
                      • Ada perubahan belum disimpan
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={closeModal} disabled={saving}>
                    Batal
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={!hasChanges || saving}
                    icon={saving ? undefined : <Save className="w-4 h-4" />}
                  >
                    {saving ? <LoadingLabel label="Menyimpan..." /> : 'Simpan Perubahan'}
                  </Button>
                </div>
              </div>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
};

export default RoleCapabilityManager;
